import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";

import PageHero from "@/components/site/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE, SERVICE_OPTIONS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_SIZES = [
  "Under 0.5 acre",
  "0.5 – 1 acre",
  "1 – 5 acres",
  "5 – 20 acres",
  "20+ acres",
];

const TIMELINES = [
  "ASAP",
  "Within 2 weeks",
  "Within 1 month",
  "1 – 3 months",
  "Flexible",
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  address: "",
  service_type: "",
  property_size: "",
  project_details: "",
  budget: "",
  timeline: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.phone.trim()) e.phone = "Phone number is required.";
    if (!form.address.trim()) e.address = "Property address is required.";
    if (!form.service_type) e.service_type = "Select a service.";
    if (!form.project_details.trim() || form.project_details.trim().length < 10)
      e.project_details = "Tell us a bit more (at least 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      toast.success("Quote request sent! We'll be in touch within 48 hours.");
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Free Quote · 48-Hour Response"
        title="Tell us about your project."
        subtitle="The more detail you share, the more accurate our quote. Prefer to talk? Call us directly — we answer the phone."
        image="https://images.pexels.com/photos/6245621/pexels-photo-6245621.jpeg"
      />

      <section className="bg-[#F9F8F6] py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:px-12">
          {/* CONTACT INFO */}
          <aside className="md:col-span-4">
            <p className="eyebrow">Direct line</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[#16302B] sm:text-4xl">
              Talk to a real person.
            </h2>
            <p className="mt-4 text-base text-[#5C5C5C]">
              Crew leaders pick up. No phone trees, no offshore call center.
            </p>

            <ul className="mt-8 space-y-5 text-sm">
              <ContactRow Icon={Phone} label="Phone" value={SITE.phone} href={SITE.phoneHref} testid="contact-phone" />
              <ContactRow Icon={Mail} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} testid="contact-email" />
              <ContactRow Icon={MapPin} label="Address" value={SITE.address} testid="contact-address" />
              <ContactRow Icon={Clock} label="Hours" value={SITE.hours} testid="contact-hours" />
            </ul>

            <div className="mt-10 border border-[#16302B]/15 bg-[#E8E2DA] p-6">
              <p className="eyebrow">Service area</p>
              <p className="mt-3 font-display text-lg font-bold leading-tight text-[#16302B]">
                {SITE.serviceArea}
              </p>
              <p className="mt-2 text-sm text-[#5C5C5C]">
                Outside our radius? Call anyway — we'll point you to a trusted crew.
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="md:col-span-8">
            <div className="border border-[#D1CCC5] bg-white p-7 md:p-12">
              {submitted ? (
                <SuccessBlock onReset={() => setSubmitted(false)} />
              ) : (
                <>
                  <p className="eyebrow">Project intake</p>
                  <h3 className="mt-3 font-display text-2xl font-black tracking-tight text-[#16302B] sm:text-3xl">
                    Request a free quote
                  </h3>
                  <p className="mt-3 text-sm text-[#5C5C5C]">
                    We'll review your details and respond within 48 hours.
                    Fields marked <span className="font-bold text-[#D95D39]">*</span> are required.
                  </p>

                  <form data-testid="quote-form" onSubmit={onSubmit} className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Full name *" error={errors.name}>
                      <Input
                        data-testid="quote-input-name"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="Jane Doe"
                        className="rounded-none border-[#D1CCC5] focus-visible:ring-[#D95D39]"
                      />
                    </Field>

                    <Field label="Email *" error={errors.email}>
                      <Input
                        type="email"
                        data-testid="quote-input-email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        placeholder="jane@example.com"
                        className="rounded-none border-[#D1CCC5] focus-visible:ring-[#D95D39]"
                      />
                    </Field>

                    <Field label="Phone *" error={errors.phone}>
                      <Input
                        type="tel"
                        data-testid="quote-input-phone"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        placeholder="(555) 555-5555"
                        className="rounded-none border-[#D1CCC5] focus-visible:ring-[#D95D39]"
                      />
                    </Field>

                    <Field label="Property address *" error={errors.address}>
                      <Input
                        data-testid="quote-input-address"
                        value={form.address}
                        onChange={(e) => setField("address", e.target.value)}
                        placeholder="123 Pine Ridge Rd, Cedar Creek"
                        className="rounded-none border-[#D1CCC5] focus-visible:ring-[#D95D39]"
                      />
                    </Field>

                    <Field label="Service needed *" error={errors.service_type}>
                      <Select value={form.service_type} onValueChange={(v) => setField("service_type", v)}>
                        <SelectTrigger
                          data-testid="quote-select-service"
                          className="rounded-none border-[#D1CCC5] focus:ring-[#D95D39]"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} data-testid={`service-option-${o.value}`}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Property size">
                      <Select value={form.property_size} onValueChange={(v) => setField("property_size", v)}>
                        <SelectTrigger
                          data-testid="quote-select-size"
                          className="rounded-none border-[#D1CCC5] focus:ring-[#D95D39]"
                        >
                          <SelectValue placeholder="Approximate acreage" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROPERTY_SIZES.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Timeline">
                      <Select value={form.timeline} onValueChange={(v) => setField("timeline", v)}>
                        <SelectTrigger
                          data-testid="quote-select-timeline"
                          className="rounded-none border-[#D1CCC5] focus:ring-[#D95D39]"
                        >
                          <SelectValue placeholder="When do you want to start?" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMELINES.map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field className="sm:col-span-2" label="Project details *" error={errors.project_details}>
                      <Textarea
                        data-testid="quote-input-details"
                        value={form.project_details}
                        onChange={(e) => setField("project_details", e.target.value)}
                        placeholder="Describe what you need — e.g., 'About 3 acres of heavy underbrush behind the barn, want it mulched for pasture. Access via gravel drive.'"
                        rows={6}
                        className="resize-none rounded-none border-[#D1CCC5] focus-visible:ring-[#D95D39]"
                      />
                    </Field>

                    <div className="sm:col-span-2 flex flex-col items-start justify-between gap-4 border-t border-[#D1CCC5] pt-6 sm:flex-row sm:items-center">
                      <p className="text-xs text-[#5C5C5C]">
                        By submitting, you agree we may contact you about your project. We never share your info.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="quote-submit-button"
                        className="inline-flex items-center gap-2 bg-[#D95D39] px-7 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#B94B2C] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {submitting ? "Sending…" : "Send Quote Request"}
                        {!submitting && <ArrowRight size={16} strokeWidth={2.5} />}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, error, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#16302B]">{label}</Label>
      {children}
      {error && (
        <p className="text-xs font-semibold text-[#D95D39]" data-testid="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

function ContactRow({ Icon, label, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center bg-[#16302B] text-[#D95D39]">
        <Icon size={16} strokeWidth={2} />
      </span>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5C5C]">{label}</p>
        <p className="mt-1 font-semibold text-[#16302B]">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <li>
      <a href={href} data-testid={testid} className="block transition-opacity hover:opacity-70">
        {content}
      </a>
    </li>
  ) : (
    <li data-testid={testid}>{content}</li>
  );
}

function SuccessBlock({ onReset }) {
  return (
    <div data-testid="quote-success" className="py-6 text-center">
      <span className="mx-auto grid h-16 w-16 place-items-center bg-[#16302B] text-[#D95D39]">
        <CheckCircle2 size={28} strokeWidth={2} />
      </span>
      <h3 className="mt-6 font-display text-2xl font-black tracking-tight text-[#16302B] sm:text-3xl">
        Quote request received.
      </h3>
      <p className="mx-auto mt-4 max-w-md text-base text-[#5C5C5C]">
        Thanks — we've logged your project. A crew lead will reach out within
        48 hours to schedule a walk-through.
      </p>
      <button
        onClick={onReset}
        data-testid="quote-submit-another"
        className="mt-8 inline-flex items-center gap-2 border-2 border-[#16302B] px-6 py-3 text-sm font-bold uppercase tracking-wider text-[#16302B] transition-colors hover:bg-[#16302B] hover:text-white"
      >
        Submit another request <ArrowRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}
